import {
    FALLBACK_SIP_PROXIES,
    FALLBACK_SIP_PROXIES_UAE,
    FALLBACK_SIP_PROXIES_UK,
    SipProxy,
} from 'flow/constants/sip-proxy-constants';
import { IS_UAE_HOST, IS_UK_HOST } from 'flow/utils/browser-utils-TW';

export type MeetingSipRegion = {
    uri: string;
    password: string;
    dialUri: string;
    proxies: SipProxy[];
};

const SWEDEN: MeetingSipRegion = {
    uri: 'sip:u013244880@sip.telavox.se',
    password: 'NoJKasJf1D',
    dialUri: 'sip:+46770339988@sip.telavox.se',
    proxies: FALLBACK_SIP_PROXIES,
};

const UAE: MeetingSipRegion = {
    uri: 'sip:u0097142167399@sip.tvx.etisalat.ae',
    password: 'IszOMX4XSy',
    dialUri: 'sip:+9718006393@sip.tvx.etisalat.ae',
    proxies: FALLBACK_SIP_PROXIES_UAE,
};

const UK: MeetingSipRegion = {
    uri: 'sip:u00441617103256@sip.telavox.co.uk',
    password: '3Ddc2uk428',
    dialUri: 'sip:+442030262290@sip.telavox.co.uk',
    proxies: FALLBACK_SIP_PROXIES_UK,
};

// Keyed by ISO 3166-1 alpha-2 country code — the same region signal the Joining Info modal uses
// (the user's own country code / the invite-link `countryCode` param). 'UK' is accepted as an alias
// for ISO 'GB'. UAE is intentionally not here — it is served only via the IS_UAE_HOST override
// below, since the UAE (Etisalat) region is isolated and unreachable from other hosts.
const TABLE: Record<string, MeetingSipRegion> = {
    SE: SWEDEN,
    GB: UK,
    UK,
};

const SUPPORTED_MEETING_REGIONS = new Set(Object.keys(TABLE));

export const toSupportedMeetingRegion = (
    countryCode?: string | null,
): string | undefined => {
    const iso = countryCode?.trim().toUpperCase();
    return iso && SUPPORTED_MEETING_REGIONS.has(iso) ? iso : undefined;
};

/**
 * Resolve the guest meeting SIP credentials, server and dial-in for the joining user's region.
 *
 * The UAE (Etisalat) deployment is an isolated region: when running as that host we always use the
 * UAE bridge. Otherwise we pick the region from the country code (resolved the same way the Joining
 * Info modal resolves it). For an unknown / missing code we default to the host's own region — UK on
 * a `.co.uk` branded host, Sweden otherwise — which preserves the previous behaviour on the standard
 * host and avoids misrouting a UK-branded app to the Swedish bridge.
 */
export const resolveMeetingSip = (
    countryCode?: string | null,
): MeetingSipRegion => {
    if (IS_UAE_HOST) {
        return UAE;
    }
    const iso = countryCode?.toUpperCase();
    return (iso && TABLE[iso]) || (IS_UK_HOST ? UK : SWEDEN);
};
